// Booking form (booking_form_v1): builds the HTML that depends on the number of buyers.
// Layout rule (approved Option 1): 1-3 buyers keep every detail on page 1; 4 or more put buyers and a
// signature row first (6 buyers a page), and flat details, terms and main signatures on the next page.
// The classes used here are styled inside the template itself (.bf1 in officeDocumentTemplates).
import { OCCUPATIONS, ageOn, formatMobile, guDigits } from '../components/buyerForm/buyerFormModel';

export type PrintBuyer = {
  /** No checked Gujarati: the block prints empty, to be filled by hand. */
  blank: boolean;
  name: string;
  age: string;
  occupation: string;
  pan: string;
  address: string;
  mobile: string;
  email: string;
};

export type GujaratiStatus = 'ok' | 'missing' | 'stale';
export const BUYERS_PER_PAGE = 6;

const esc = (value: unknown) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const same = (a: unknown, b: unknown) =>
  String(a ?? '').trim().replace(/\s+/g, ' ').toLowerCase() === String(b ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/** Approved Gujarati usable for this buyer? 'stale' = name or address was changed after approval. */
export const gujaratiStatus = (customer: any): GujaratiStatus => {
  const gu = customer?.gujarati;
  if (!gu?.name) return 'missing';
  const from = gu.from;
  if (!from) return 'ok'; // Approved before the English was saved alongside; nothing to compare with.
  const name = customer?.displayName || customer?.name;
  const parts = ['addressLine1', 'addressLine2', 'city', 'state'];
  return same(from.name, name) && parts.every((p) => same(from[p], customer?.[p])) ? 'ok' : 'stale';
};

const formDay = (ymd: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date();
};

export const printBuyerFromCustomer = (customer: any, formDateYmd: string): PrintBuyer => {
  if (gujaratiStatus(customer) !== 'ok') return { blank: true, name: '', age: '', occupation: '', pan: '', address: '', mobile: '', email: '' };
  const gu = customer.gujarati;
  const age = ageOn(String(customer.dateOfBirth || customer.dob || ''), formDay(formDateYmd));
  const fixed = OCCUPATIONS.find((o) => o.value === customer.occupation)?.gu;
  const place = [gu.addressLine1, gu.addressLine2, gu.city, gu.state].filter(Boolean).join(', ');
  const mobile = String(customer.mobile || '').replace(/\D/g, '').slice(-10);
  return {
    blank: false,
    name: String(gu.name),
    age: age == null ? '' : guDigits(age),
    occupation: fixed || String(gu.occupationOther || ''),
    pan: String(customer.pan || ''),
    address: `${place}${customer.pincode ? ` - ${guDigits(customer.pincode)}` : ''}`,
    mobile: mobile ? formatMobile(mobile) : '',
    email: String(customer.email || ''),
  };
};

const cell = (label: string, value: string, opts: { span?: number; lat?: boolean } = {}) =>
  `<td${opts.span ? ` colspan="${opts.span}"` : ''}>${label}: <b${opts.lat ? ' class="lat"' : ''}>${esc(value)}</b></td>`;

const buyerTable = (b: PrintBuyer, index: number) =>
  `<table class="b${b.blank ? ' blank' : ''}"><colgroup><col class="c0"><col><col><col></colgroup><tbody>` +
  `<tr><td rowspan="4" class="no">(${guDigits(index + 1)})</td>${cell('નામ', b.name, { span: 3 })}</tr>` +
  `<tr>${cell('ઉંમર (વર્ષ)', b.age)}${cell('ધંધો', b.occupation)}${cell('PAN', b.pan, { lat: true })}</tr>` +
  `<tr>${cell('રહેવાસી', b.address, { span: 3 })}</tr>` +
  `<tr>${cell('મોબાઇલ', b.mobile, { lat: true })}${cell('ઈ-મેઈલ', b.email, { span: 2, lat: true })}</tr>` +
  '</tbody></table>';

const signLabel = (index: number) => `ખરીદનાર (${guDigits(index + 1)}) ની સહી`;
const smallSigns = (buyers: PrintBuyer[], from: number) =>
  `<div class="signs" style="grid-template-columns:repeat(${buyers.length},1fr)">` +
  buyers.map((_, i) => `<div class="sign sm"><span>${signLabel(from + i)}</span></div>`).join('') + '</div>';

// Height budget per page in mm (page 1 also carries the title and note). A block is about 25 mm, plus a line for a long address.
const blockMm = (b: PrintBuyer) => (b.blank ? 45 : 25 + (b.address.length > 78 ? 5 : 0));
/** Buyers per page: at most 6, fewer when long addresses would push the signature row off the page. */
export const buyerPages = (buyers: PrintBuyer[]): PrintBuyer[][] => {
  const pages: PrintBuyer[][] = [];
  let page: PrintBuyer[] = [];
  let used = 0;
  buyers.forEach((b) => {
    const budget = pages.length === 0 ? 165 : 205;
    if (page.length && (page.length >= BUYERS_PER_PAGE || used + blockMm(b) > budget)) {
      pages.push(page);
      page = [];
      used = 0;
    }
    page.push(b);
    used += blockMm(b);
  });
  if (page.length) pages.push(page);
  return pages;
};

/** Spacing class on the form: 'single' keeps 1-3 buyers on page 1, 'many' tightens pages with many signatures. */
export const layoutClass = (buyerCount: number) => (buyerCount <= 3 ? 'single' : buyerCount > 6 ? 'many' : 'multi');

/** Everything for the buyers section, including page breaks and page signature rows when there are 4 or more. */
export const buyersBlock = (buyers: PrintBuyer[]): string => {
  const list = buyers.length ? buyers : [printBuyerFromCustomer(null, '')];
  const bar = `<table class="bar"><tbody><tr><th>ખરીદનારની વિગત<span class="r">${guDigits(list.length)} ખરીદનાર</span></th></tr></tbody></table>`;
  if (list.length <= 3) return bar + list.map(buyerTable).join('');
  let html = '';
  let from = 0;
  const pages = buyerPages(list);
  pages.forEach((chunk, p) => {
    const start = from;
    html += (p === 0 ? bar : '') + chunk.map((b, i) => buyerTable(b, start + i)).join('') + smallSigns(chunk, start) +
      `<div class="cont">${p === pages.length - 1 ? 'ફ્લેટની વિગત અને પેમેન્ટની શરતો આગળના પાના પર' : 'બાકીના ખરીદનાર આગળના પાના પર'}</div><div class="pb"></div>`;
    from += chunk.length;
  });
  return html;
};

/** Main signatures beside the flat details: one row with the date for 1-3 buyers, two per row after that. */
export const mainSignatures = (buyers: PrintBuyer[], formDate: string): string => {
  const list = buyers.length ? buyers : [printBuyerFromCustomer(null, '')];
  if (list.length <= 3) {
    return `<div class="signs" style="grid-template-columns:repeat(${list.length},1fr) 32mm">` +
      list.map((_, i) => `<div class="sign sm"><span>${signLabel(i)}</span></div>`).join('') +
      `<div class="sign sm"><span>તારીખ: <b class="lat">${esc(formDate)}</b></span></div></div>`;
  }
  return '<div class="signs" style="grid-template-columns:1fr 1fr">' +
    list.map((b, i) => `<div class="sign"><span>${signLabel(i)}${b.name ? ` · ${esc(b.name)}` : ''}</span></div>`).join('') +
    `</div><div class="meta-line"><span>તારીખ: <b class="lat">${esc(formDate)}</b></span><span>સ્થળ: <b>સુરત</b></span></div>`;
};

/** Signature lines for the terms page (date filled) and the blank payment page (date left to fill). */
export const signatureLines = (buyers: PrintBuyer[], formDate: string | null): string => {
  const list = buyers.length ? buyers : [printBuyerFromCustomer(null, '')];
  const meta = formDate == null
    ? '<span>તારીખ: <i class="blank-line"></i></span><span>સ્થળ: <i class="blank-line"></i></span>'
    : `<span>તારીખ: <b class="lat">${esc(formDate)}</b></span><span>સ્થળ: <b>સુરત</b></span>`;
  return `<div class="meta-line">${meta}</div><div class="lines" style="grid-template-columns:repeat(${Math.min(list.length, 6)},1fr)">` +
    list.map((_, i) => `<div><div class="line">${signLabel(i)}</div></div>`).join('') + '</div>';
};

const tick = (on: boolean, label: string) => `<span class="box${on ? ' on' : ''}"></span>${label}`;
export const committeeBoxes = (value: unknown) =>
  `${tick(value === 'yes', 'હા')} ${tick(value === 'no', 'ના')}`;
export const documentsChecklist = (docs: { photos?: boolean; pan?: boolean; aadhaar?: boolean } | null | undefined) =>
  [
    [docs?.photos, 'ખરીદનારના બે પાસપોર્ટ ફોટા (દરેક ખરીદનાર)'],
    [docs?.pan, 'પાન કાર્ડ ઝેરોક્ષ (૩ નકલ)'],
    [docs?.aadhaar, 'આધાર કાર્ડ ઝેરોક્ષ (૩ નકલ)'],
  ].map(([on, label]) => `<div>${tick(!!on, String(label))}</div>`).join('');

/** The flat line in the details heading, leaving out whatever a unit does not have. */
export const flatLine = (unitNo: string, wing: string, floor: string) => {
  const f = String(floor || '').trim();
  return [`<span class="lat">${esc(unitNo)}</span>`, wing ? `<span class="lat">${esc(wing)}</span>` : '', f ? `માળ: <span class="lat">${esc(f)}</span>` : '']
    .filter(Boolean).join(', ');
};

/** Page the terms print on, for the date stamp in the footer. */
export const termsPageNumber = (buyers: PrintBuyer[]) =>
  buyers.length <= 3 ? 2 : buyerPages(buyers).length + 2;
